/**
 * pages/high_risk/prework15/index.js — ӨЭА ажилтны 15 чек листийн
 * Pre-Work шалгалт.
 *
 * Зам: /highrisk/prework15
 *
 * Урсгал:
 *   1. АШУА ажилтанг ӨЭА-н нарядаар бүртгэсний дараа тус ажилтан системд
 *      орж ирэх үед энэ хуудас гарч ирнэ.
 *   2. Чухал хяналтын 15 асуумжийг бөглөнө.
 *      Асуумж нь "Энэ аюул байна уу?" гэж асуудаг тул
 *      Тийм = АЮУЛ БАЙНА · Үгүй = аюулгүй · Хамаарахгүй = алгасах.
 *   3. Аль нэг асуумж дээр "Тийм" сонгох тохиолдолд → "Ажил зогсоох" модал
 *      нээгдэж, аюулыг бүртгэнэ (HazardFormComposer mode="stop_work").
 *      Бүртгэсний дараа хуудсан дээрээ үлдэж үлдсэн асуумжаа үргэлжлүүлнэ.
 *   4. Бүх асуумжид хариулсан үед илгээх товч идэвхэжнэ. Аюул илэрсэн ч
 *      ажилтныг түгжихгүй — хариулт нь ӨЭА бүртгэлд
 *      (checklist_15_completed/_answers) хадгалагдаж, АШУА/анхан шатны
 *      удирдлага ажилд гаргах эсэхийг шийднэ.
 *
 * 15 жагсаалт — [[checklistStore]] (Чухал хяналт.pptx-аас seed).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Progress,
  Result,
  message as antMessage,
  Tag,
} from "antd";
import Swal from "sweetalert2";

import HazardFormComposer from "src/components/hazard";
import { xabHazardApi } from "src/api/xabApi";
import {
  Card,
  RED,
  PAGE_BG,
  hazardCategoryIds,
} from "src/components/hazard/common";
import { loadChecklist, listChecklist } from "src/pages/risk/reference/checklistStore";
import { useUserContext } from "src/contexts/userContext";
import { getMyOeaRegistration } from "src/api/hazard";
import {
  getCategories,
  getSeverities,
  createHazard,
  uploadHazardPhoto,
  createPreworkSubmission,
  listOeaRegistrations,
  getHazards,
} from "src/api/hazard";
import PageHelp from "src/components/help/PageHelp";
// ⚠ АШУА-гийн төлөв localStorage-аас уншихаа БОЛИВ. ӨЭА бүртгэл одоо
//   зөвхөн баазад бичигддэг тул тэр сан хоосон — уншсаар байвал
//   «АШУА биш» гэж буруу дүгнэж, давхар хяналт огт гарахгүй болно.
import { listPreworkSubmissions } from "src/api/hazard";

// Аюулын төлөвийн шошго — hazard/Dashboard-тай ижил утга.
const HZ_STATUS = {
  NEW: { name: "Шинэ", bg: "#FDECEC", fg: "#DC2626" },
  TRIAGE: { name: "Хяналтад", bg: "#E4F0F8", fg: "#1F4E79" },
  ASSIGNED: { name: "Хуваарилсан", bg: "#E4F0F8", fg: "#1F4E79" },
  IN_PROGRESS: { name: "Хэрэгжиж буй", bg: "#FEF6E7", fg: "#CA8A04" },
  COMPLETED: { name: "Биелсэн", bg: "#E7F6EC", fg: "#16A34A" },
  REJECTED: { name: "Татгалзсан", bg: "#F1F2F4", fg: "#6B7280" },
};
// Шийдвэр ГАРААГҮЙ гэж үзэх төлөвүүд — ажил хараахан сэргээгдээгүй
const HZ_OPEN_STATUSES = ["NEW", "TRIAGE", "ASSIGNED", "IN_PROGRESS"];
const isHazardOpen = (h) =>
  HZ_OPEN_STATUSES.includes(String(h?.status || "NEW").toUpperCase());

// Чухал хяналтын асуумж "Энэ аюул байна уу?" гэж асуудаг учир —
//   • "Тийм"        → АЮУЛ БАЙНА → улаан, "Ажил зогсоох" нээх
//   • "Үгүй"        → Аюул байхгүй → ногоон, OK
//   • "Хамаарахгүй"  → энэ асуумж тус ажилд хамаарахгүй → саарал
/**
 * ТН тулгалт — тэргүүн тэг зөрдөг (017824 ↔ 17824).
 *
 * ⚠ Түүхийгээр тэнцүүлбэл АШУА өөрийн бүртгэсэн ажилтныг олохгүй,
 *   давхар хяналт огт шаардагдахгүй болно. Backend тал ч ижил дүрэмтэй
 *   (`Models/inspection/assignment_rules.py` · `same_tn`).
 */
const sameTn = (a, b) => {
  const d = (v) => String(v || "").replace(/[^0-9]/g, "").replace(/^0+/, "");
  return !!d(a) && d(a) === d(b);
};

const ANSWERS = {
  YES: { key: "YES", label: "Тийм", color: "#DC2626", bg: "#FEEFED" },
  NO: { key: "NO", label: "Үгүй", color: "#16A34A", bg: "#EAF6EE" },
  NA: { key: "NA", label: "Хамаарахгүй", color: "#64748B", bg: "#F1F3F6" },
};

const todayDateStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * АШУА-гийн өнөөдрийн бүртгэлээс ӨЭА АЖЛУУДыг ялгаж авна.
 *
 * АШУА нь ажилтан бүрийг тусад нь бүртгэдэг ч тэд НЭГ ажлын дугаар
 * (oea_permit_id) дээр цугладаг. Давхар хяналт нь ажилтанд биш АЖИЛД
 * хамаарах тул oea_permit_id-аар бүлэглэнэ.
 *
 * Зөвхөн ӨЭА (наряд ЗӨВШӨӨРӨЛ) ажил — ажлын ДААЛГАВАР (NARYAD) нь 5А-гийн
 * харьяа бөгөөд АШУА-гийн давхар 15А шаардахгүй.
 */
const groupAshuaJobs = (rows, myTn) => {
  const tn = String(myTn || "").trim();
  const byJob = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (String(r.insert_user_tn || "").trim() !== tn) return; // өөрийн бүртгэл
    if (r.work_type === "NARYAD") return; // 5А-гийн харьяа
    const key = r.oea_permit_id ?? `noid-${r.department_code || ""}-${r.destination || ""}`;
    const cur = byJob.get(key) || {
      key,
      oea_permit_id: r.oea_permit_id ?? null,
      department_code: r.department_code,
      department_name: r.department_name,
      section_code: r.section_code,
      section_name: r.section_name,
      destination: r.destination,
      work_description: r.work_description,
      workers: [],
    };
    cur.workers.push(r);
    byJob.set(key, cur);
  });
  return [...byJob.values()];
};

/**
 * PriorHazardNote — тухайн асуумж дээр УРЬД бүртгэсэн аюулын товч мэдээлэл.
 *
 * Ажилтан 15А-г дахин нээхэд «энэ үзүүлэлт дээр аюул бүртгэсэн, шийдвэр
 * хүлээгдэж байна» гэдгийг шууд харна — дахин бүртгэх шаардлагагүй.
 */
const PriorHazardNote = ({ h }) => {
  const st = HZ_STATUS[String(h.status || "NEW").toUpperCase()] || {
    name: h.status || "—",
    bg: "#F1F2F4",
    fg: "#6B7280",
  };
  const open = isHazardOpen(h);
  return (
    <div
      style={{
        marginTop: 8,
        padding: "7px 10px",
        borderRadius: 8,
        background: open ? "#FEEFED" : "#F5F7FA",
        border: `1px solid ${open ? "#F6C9C5" : "#E2E6EE"}`,
        fontSize: 11.5,
        lineHeight: 1.5,
        color: open ? "#8C2A22" : "#5B6472",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <i className="fas fa-exclamation-triangle" />
        <b>Аюул бүртгэгдсэн</b>
        <span style={{ fontFamily: "monospace", opacity: 0.85 }}>
          {h.report_number || `#${h.id}`}
        </span>
        <span
          style={{
            padding: "1px 8px",
            borderRadius: 999,
            background: st.bg,
            color: st.fg,
            fontWeight: 800,
            fontSize: 10.5,
          }}
        >
          {st.name}
        </span>
        {h.report_date ? (
          <span style={{ opacity: 0.75 }}>
            {String(h.report_date).slice(0, 16).replace("T", " ")}
          </span>
        ) : null}
      </div>
      {h.description ? (
        <div style={{ marginTop: 3, opacity: 0.9 }}>{h.description}</div>
      ) : null}
      <div style={{ marginTop: 3, fontWeight: 700 }}>
        {open
          ? "Шийдвэр хүлээгдэж байна — аюул арилтал ажлыг эхлүүлэхгүй."
          : "Шийдвэрлэгдсэн."}
      </div>
    </div>
  );
};

export default function PreWork15() {
  const navigate = useNavigate();
  const { user } = useUserContext();

  // Чек листийн 15 асуумж — DB-ээс (кэшээс шууд + шинэчилнэ)
  const [items, setItems] = useState(() => listChecklist("CRITICAL"));
  useEffect(() => {
    loadChecklist("CRITICAL").then(setItems);
  }, []);

  // Хариултууд — { itemId → "YES" | "NO" | "NA" }
  const [answers, setAnswers] = useState({});

  // ӨЭА бүртгэл (АШУА бүртгэсэн өнөөдрийн)
  const [oeaToday, setOeaToday] = useState(null);
  const [loadingOea, setLoadingOea] = useState(true);

  // Аюул мэдээлэх / Ажил зогсоох модал
  const [hazardOpen, setHazardOpen] = useState(false);
  const [hazardForm, setHazardForm] = useState({});
  const [hazardFlagged, setHazardFlagged] = useState(null); // ямар асуумж дээр
  const [saving, setSaving] = useState(false);
  // Аль асуумж дээр аюулыг АЛЬ ХЭДИЙН бүртгэсэн — { itemId: hazardReportId }.
  // Бүртгэсний дараа хуудаснаас гаргахгүй, үлдсэн асуумжаа бөглөж
  // шалгалтаа илгээх боломжтой байх ёстой. hazard_report_id-г хадгалснаар
  // илгээхэд «аль үзүүлэлт дээр аль аюул» гэдэг мөрөөрөө холбогдоно.
  const [reported, setReported] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── АШУА-гийн ДАВХАР ХЯНАЛТ — аль ӨЭА ажил дээр хийж байгаа вэ ────────
  // АШУА өөрөө ӨЭА-гаар бүртгэгддэггүй тул ажлын дугаарыг нь бүртгэлээс
  // нь гаргаж авахгүй. Өнөөдөр өөрөө бүртгэсэн ажилтнуудын ӨЭА ажлуудыг
  // жагсааж, аль дээр нь хяналт хийж байгааг сонгуулна. Эс бөгөөс
  // давхар хяналт ажлын дугаарт холбогдохгүй өнчин мөр болно.
  const [ashuaJobs, setAshuaJobs] = useState([]);
  const [ashuaJobId, setAshuaJobId] = useState(null); // сонгосон oea_permit_id

  // ── ЭНЭ АЖИЛ дээр УРЬД бүртгэгдсэн аюулууд — { item_no: hazard } ──────
  // Ажилтан 15А-г дахин нээхэд «энэ үзүүлэлт дээр аюул бүртгэгдсэн, шийдвэр
  // хүлээгдэж байна» гэдгийг харах ёстой. Эс бөгөөс нэг аюулыг дахин дахин
  // бүртгэх эрсдэлтэй.
  const [priorHazards, setPriorHazards] = useState({});

  // Lookup
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [hazardList, setHazardList] = useState([]); // xab.hazards эталон жагсаалт

  // Одоогийн нэвтэрсэн хэрэглэгчийн ТН — backend mine=true болон localStorage
  // fallback-д ашиглана
  const myTn = String(user?.info?.tn || user?.tn || "");

  // АШУА горим — өнөөдөр өөрийн ТН-ээр ажилтан бүртгэсэн эсэх.
  // Хэрэв тийм бол хариултыг АШУА -ийн нэрийн доор хадгална (нэг өдөрт нэг).
  // ⚠ АШУА байх ба ажилтан байх нь ХАРИЛЦАН ҮГҮЙСГЭХГҮЙ. Нэг хүн өнөөдөр
  // бусдыг ӨЭА-д бүртгэсэн (АШУА) БАЙЖ, зэрэг өөрөө ч тэр ажилд
  // бүртгэгдсэн (ажилтан) байж болно — өөрийгөө бүртгэсэн тохиолдол
  // байнга гардаг.
  //
  // Өмнө нь isAshua=true бол ажилтны урсгалыг БҮРМӨСӨН унтраадаг байсан тул
  // тийм хүний бөглөлт үргэлж checker_role='ASHUA' болж, өөрийн 15А
  // (WORKER) хэзээ ч үүсдэггүй → /me дээр "бөглөх" мөнхөд харагддаг байв.
  // ── АШУА-гийн өнөөдрийн төлөв — БААЗААС ──────────────────────────
  //   `ashuaWorkers` — өнөөдөр МИНИЙ бүртгэсэн ажилтнууд
  //   `ashuaPrev`    — миний өөрийн АШУА 15А бөглөлт (байвал)
  // ⚠ Асинхрон тул эхний зурагт хоосон. Урьд нь localStorage-аас sync
  //   уншдаг байсан; одоо мэдээлэл ирэхэд үүргийн сэлгэлт гарч ирнэ.
  const [ashuaWorkers, setAshuaWorkers] = useState([]);
  const [ashuaPrev, setAshuaPrev] = useState(null);
  const canBeAshua = ashuaWorkers.length > 0;

  // Одоо аль үүргээр бөглөж байгаа: "WORKER" | "ASHUA"
  const [checkerRole, setCheckerRole] = useState(null);
  const isAshua = checkerRole === "ASHUA";

  useEffect(() => {
    if (!myTn) return;
    let alive = true;
    const day = todayDateStr();
    (async () => {
      try {
        const rows = await listOeaRegistrations({ work_date: day });
        const mine = (Array.isArray(rows) ? rows : []).filter(
          (r) => sameTn(r.insert_user_tn, myTn) && r.work_date === day,
        );
        if (!alive) return;
        setAshuaWorkers(mine);

        if (mine.length) {
          const subs = await listPreworkSubmissions({
            tn: myTn,
            work_date: day,
            checker_role: "ASHUA",
            checklist_type: "CRITICAL",
          });
          const list = subs?.items || subs?.data || subs || [];
          if (alive) {
            setAshuaPrev(Array.isArray(list) && list.length ? list[0] : null);
          }
        } else if (alive) {
          setAshuaPrev(null);
        }
      } catch (err) {
        // ⚠ Чимээгүй болгохгүй: АШУА эсэхээ мэдэхгүй бол давхар хяналт
        //   алгасагдана. Лог үлдээж, шалтгааныг мөшгих боломж өгнө.
        console.warn("АШУА төлөв уншигдсангүй:", err?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [myTn]);

  useEffect(() => {
    (async () => {
      // Хоёуланг нь ЗЭРЭГ татна — хүн нэгэн зэрэг ажилтан ба АШУА байж болно.
      try {
        // ТН-ийг параметрт өгснөөр localStorage fallback ажиллана —
        // АШУА вэбээс бүртгэсэн боловч backend бэлэн биш үед ч засагдана.
        // Энэ бол ӨНДӨР ЭРСДЭЛТ (15 асуумж) хуудас. Зөвхөн ӨЭА (наряд
        // зөвшөөрөл) бүртгэлийг хүлээн авна. NARYAD (ажлын даалгавар) бол
        // 5А-гийн харьяа тул энд хүлээж авахгүй.
        const r = await getMyOeaRegistration({ tn: myTn }).catch(() => null);
        const own = r && r.work_type !== "NARYAD" ? r : null;
        setOeaToday(own);

        // АШУА бол давхар хяналт хийх ӨЭА ажлуудаа татна
        let jobs = [];
        if (canBeAshua) {
          try {
            const rows = await listOeaRegistrations({
              work_date: todayDateStr(),
            });
            jobs = groupAshuaJobs(rows, myTn);
          } catch {
            jobs = [];
          }
          setAshuaJobs(jobs);
        }

        // ── Анхдагч үүрэг ──
        // Өөрийн ӨЭА бүртгэлтэй бол ЭХЛЭЭД өөрийн 15А-г бөглөнө — энэ нь
        // ажилд орох нөхцөл. АШУА-гийн давхар хяналтыг дараа нь сэлгэж
        // хийнэ. Өөрийн бүртгэлгүй, зөвхөн бусдыг бүртгэсэн бол ASHUA.
        setCheckerRole(own ? "WORKER" : canBeAshua ? "ASHUA" : "WORKER");

        // ── Урьд бүртгэсэн аюулуудаа татах ──
        // «Энэ ажлын энэ үзүүлэлт дээр аюул бүртгэгдсэн» гэдгийг хуудас
        // дахин нээх бүрд харуулна. Шийдвэр гараагүй (нээлттэй) аюулыг
        // онцолж, дахин бүртгэхийг шаардахгүй.
        try {
          const res = await getHazards({ mine: 1, per_page: 100 });
          const rows = Array.isArray(res?.items) ? res.items : [];
          const map = {};
          rows.forEach((h) => {
            if (h.prework_item_type !== "CRITICAL") return;
            if (h.prework_item_no == null) return;
            // Зөвхөн ЭНЭ ажлынх — бүртгэлийн эсвэл ажлын дугаараар
            const sameWork = own
              ? String(h.oea_registration_id || "") === String(own.id)
              : true;
            if (!sameWork) return;
            const no = Number(h.prework_item_no);
            // Нэг үзүүлэлт дээр олон бол хамгийн сүүлийнх
            if (!map[no] || Number(h.id) > Number(map[no].id)) map[no] = h;
          });
          setPriorHazards(map);
        } catch {
          setPriorHazards({});
        }
      } finally {
        setLoadingOea(false);
      }
    })();
    (async () => {
      try {
        const [cat, sev, hz] = await Promise.all([
          getCategories(),
          getSeverities(),
          xabHazardApi.list().catch(() => []),
        ]);
        setCategories(cat.items || []);
        setSeverities(sev.items || []);
        setHazardList(Array.isArray(hz) ? hz : hz?.items || []);
      } catch {
        /* алгасах */
      }
    })();
  }, []);

  const answered = useMemo(
    () => items.filter((it) => answers[it.id]).length,
    [items, answers],
  );
  // "Тийм" — аюул байна гэсэн хариулт. Энэ нь өсөх тусам асуудалтай.
  const yesCount = useMemo(
    () => items.filter((it) => answers[it.id] === "YES").length,
    [items, answers],
  );
  const total = items.length;
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  const isComplete = answered === total;

  const setAnswer = (id, key) => {
    setAnswers((prev) => ({ ...prev, [id]: key }));
    // "Тийм" — аюул байна → шууд "Ажил зогсоох" модал нээх
    if (key === "YES") {
      openHazardReport(items.find((it) => it.id === id));
    }
  };

  const openHazardReport = (item) => {
    setHazardFlagged(item);
    // Аль асуумж дээр аюул илэрснийг модалын ГАРЧИГ дээр харуулна
    // (доорх Modal title). Тайлбар талбар нь зөвхөн ажилтны бичсэн текст
    // эсвэл дуу хоолойн хөрвүүлэг байх ёстой тул хоосон эхэлнэ.
    setHazardForm({
      photos: [],
      description: "",
      severity: 3,
      // ӨЭА бүртгэлээс цех/хэсэг автомат
      deptCode: oeaToday?.department_code || "",
      deptName: oeaToday?.department_name || "",
      sectionCode: oeaToday?.section_code || "",
      sectionName: oeaToday?.section_name || "",
      locationText: oeaToday?.destination || "",
      selfInDanger: true,
    });
    setHazardOpen(true);
  };

  const closeHazard = () => {
    setHazardOpen(false);
    setHazardFlagged(null);
  };

  const submitHazardStop = async () => {
    if (!hazardForm.description?.trim()) {
      Swal.fire({
        title: "Аюулын тайлбарыг бичнэ үү",
        text: "Гараар бичих эсвэл дуу хоолойгоор ярьж бичүүлнэ үү.",
        icon: "warning",
      });
      return;
    }
    setSaving(true);
    try {
      const created = await createHazard({
        location_text: hazardForm.locationText,
        gps_lat: hazardForm.gps?.lat,
        gps_lng: hazardForm.gps?.lng,
        gps_accuracy: hazardForm.gps?.accuracy,
        hazard_category_id: hazardForm.categoryId
          ? Number(hazardForm.categoryId)
          : undefined,
        // Композер 1-3 төрөл сонгуулдаг (categoryIds). Зөвхөн categoryId
        // илгээвэл 2 дахь нь замдаа алдагдана — Register.js-тэй ижилээр
        // бүх сонголтыг таслалаар илгээнэ.
        hazard_category_ids: hazardCategoryIds(hazardForm),
        // xab.hazards-аас сонгосон эталон аюулын id → hazard_reports.hazard_id
        hazard_id: hazardForm.hazardId
          ? Number(hazardForm.hazardId)
          : undefined,
        description: hazardForm.description.trim(),
        voice_file_url: hazardForm.voiceFileUrl || undefined,
        severity: hazardForm.severity,
        affected_location:
          hazardForm.affectedLocation || hazardForm.locationText,
        affected_people_count: hazardForm.peopleCount || undefined,
        self_in_danger: !!hazardForm.selfInDanger,
        photos: hazardForm.photos || [],
        report_mode: "stop_work",
        meta: {
          prework15_item_id: hazardFlagged?.id,
          prework15_item_no: hazardFlagged?.no,
          oea_registration_id: oeaToday?.id,
        },
        offline_client_id:
          "stop-" +
          Date.now() +
          "-" +
          Math.random().toString(36).slice(2, 8),
      });
      // Тухайн асуумжийг «бүртгэсэн» гэж тэмдэглээд хуудсан дээрээ үлдэнэ —
      // үлдсэн асуумжаа бөглөж шалгалтаа илгээх ёстой.
      // Аюулын id-г хадгална: илгээхэд асуумжийн мөртэй холбоно
      // (prework_checklist_answer.hazard_report_id).
      if (hazardFlagged?.id) {
        setReported((p) => ({
          ...p,
          [hazardFlagged.id]: created?.id || true,
        }));
      }
      closeHazard();
      await Swal.fire({
        icon: "warning",
        title: "Ажил зогсоосон",
        text:
          "Аюул бүртгэгдэж АШУА болон ХАБ хэлтэст илгээгдлээ. Шийдвэр гартал ажлыг хийхгүй. "
          + "Үлдсэн асуумжаа бөглөж шалгалтаа илгээнэ үү.",
      });
    } catch (err) {
      antMessage.error("Илгээхэд алдаа гарлаа");
    } finally {
      setSaving(false);
    }
  };

  // Урьд бүртгэсэн аюулыг асуумжид буулгах: тухайн үзүүлэлтийг «Тийм»
  // (аюул байна) гэж тэмдэглэж, «бүртгэсэн» гэж үзнэ. Ингэснээр ажилтан
  // нэг аюулыг давхар бүртгэхгүй, «бүртгээгүй аюул байна» гэж гуйхгүй.
  useEffect(() => {
    const noMap = {};
    items.forEach((it) => {
      if (it?.no != null) noMap[Number(it.no)] = it.id;
    });
    const ans = {};
    const rep = {};
    Object.entries(priorHazards).forEach(([no, h]) => {
      const itemId = noMap[Number(no)];
      if (itemId == null) return;
      ans[itemId] = "YES";
      rep[itemId] = h.id;
    });
    if (!Object.keys(rep).length) return;
    // Ажилтны гараар өгсөн хариултыг ДАРЖ БИЧИХГҮЙ — зөвхөн дутууг нөхнө
    setAnswers((prev) => ({ ...ans, ...prev }));
    setReported((prev) => ({ ...rep, ...prev }));
  }, [items, priorHazards]);

  // Шийдвэр ГАРААГҮЙ (нээлттэй) аюулууд — асуумжийн дугаараар эрэмбэлсэн
  const openPriorHazards = useMemo(
    () =>
      Object.values(priorHazards)
        .filter(isHazardOpen)
        .sort(
          (a, b) => Number(a.prework_item_no) - Number(b.prework_item_no),
        ),
    [priorHazards],
  );

  // АШУА-гийн сонгосон ажил (эсвэл ганцхан ажил байвал автоматаар тэр)
  const ashuaJob = useMemo(() => {
    if (!isAshua || ashuaJobs.length === 0) return null;
    if (ashuaJobs.length === 1) return ashuaJobs[0];
    return ashuaJobs.find((j) => j.key === ashuaJobId) || null;
  }, [isAshua, ashuaJobs, ashuaJobId]);

  // "Тийм" (аюул байна) гэж хариулсан ч аюулаа хараахан бүртгээгүй асуумжууд
  const unreportedYes = useMemo(
    () => items.filter((it) => answers[it.id] === "YES" && !reported[it.id]),
    [items, answers, reported],
  );

  /**
   * Шалгалтыг ИЛГЭЭХ.
   * Аюул илэрсэн ч ажилтныг ТҮГЖИХГҮЙ — 5А-тай ижил зарчим: бүх хариулт
   * бүртгэгдэж АШУА/анхан шатны удирдлагад очиж, тэд ажилд гаргах эсэхийг
   * шийднэ. (Өмнө нь yesCount > 0 үед бүрмөсөн хааж, аюул бүртгэсэн хүн
   * асуумжаа хэзээ ч дуусгаж чаддаггүй байв.)
   */
  const completePreWork = async () => {
    if (!isComplete) {
      antMessage.warning("Бүх асуумжид хариулна уу");
      return;
    }
    // АШУА-гийн давхар хяналт нь ЗААВАЛ ажлын дугаарт холбогдоно —
    // эс бөгөөс хаана хийсэн нь мэдэгдэхгүй өнчин бүртгэл болно.
    if (isAshua && ashuaJobs.length > 0 && !ashuaJob) {
      antMessage.warning("Аль ӨЭА ажил дээр хяналт хийснээ сонгоно уу");
      return;
    }
    // Аюул илрүүлсэн асуумж бүр дээр "Ажил зогсоох" бүртгэл үүссэн байх ёстой
    if (unreportedYes.length > 0) {
      const r = await Swal.fire({
        icon: "warning",
        title: "Бүртгээгүй аюул байна",
        html:
          "Дараах асуумж дээр аюул илэрсэн ч бүртгэл үүсээгүй байна:<br/><b>#"
          + unreportedYes.map((it) => it.no).join(", #")
          + "</b>",
        showCancelButton: true,
        confirmButtonText: "Одоо бүртгэх",
        cancelButtonText: "Болих",
      });
      if (r.isConfirmed) openHazardReport(unreportedYes[0]);
      return;
    }
    if (yesCount > 0) {
      const ok = await Swal.fire({
        icon: "warning",
        title: "Аюул илрүүлсэн байна",
        text:
          `${yesCount} асуумж дээр аюул илрүүлж бүртгэсэн. Хариултууд тань `
          + "АШУА болон анхан шатны удирдах ажилтанд илгээгдэж, ажилд гаргах "
          + "эсэхийг тэд шийднэ. Илгээх үү?",
        showCancelButton: true,
        confirmButtonText: "Илгээх",
        cancelButtonText: "Болих",
      });
      if (!ok.isConfirmed) return;
    }

    setSubmitting(true);
    try {
      // ── Бөглөлтийг xab.prework_checklist_submission-д хадгална ──
      // Мөр бүр = НЭГ хүний хийсэн НЭГ шалгалт. АШУА нь ажилтны ӨМНӨӨС
      // бөглөхгүй — ӨЭА ажил дээр очиж ӨӨРИЙН 15А-гаа бөглөнө, тиймээс
      // checker_role="ASHUA" гэсэн БИЕ ДААСАН мөр үүснэ.
      const res = await createPreworkSubmission({
        work_type: "OEA", // 15А нь зөвхөн ӨЭА (наряд зөвшөөрөл) ажилд
        checklist_type: "CRITICAL",
        checker_role: isAshua ? "ASHUA" : "WORKER",
        // АШУА — сонгосон ажлын дугаар. Ажилтан — өөрийн бүртгэлээс.
        oea_permit_id: isAshua
          ? ashuaJob?.oea_permit_id
          : oeaToday?.oea_permit_id,
        // АШУА-гийн давхар хяналт нь ажилтны мөрд наалдахгүй —
        // oea_registration_id илгээхгүй (сервер тольдолт хийхгүй).
        oea_registration_id: isAshua ? undefined : oeaToday?.id,
        work_date: todayDateStr(),
        tn: myTn,
        department_code: (isAshua ? ashuaJob : oeaToday)?.department_code,
        department_name: (isAshua ? ashuaJob : oeaToday)?.department_name,
        section_code: (isAshua ? ashuaJob : oeaToday)?.section_code,
        section_name: (isAshua ? ashuaJob : oeaToday)?.section_name,
        destination: (isAshua ? ashuaJob : oeaToday)?.destination,
        source: "WEB",
        answers: items.map((it) => ({
          checklist_item_id: it.id,
          item_code: String(it.id),
          item_no: it.no,
          item_title: it.title,
          answer: answers[it.id] || null,
          // «Аль үзүүлэлт дээр аль аюул» — бүртгэсэн аюулын id
          hazard_report_id:
            typeof reported[it.id] === "number" ? reported[it.id] : undefined,
        })),
        offline_client_id:
          "pw15-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
      });

      // ⚠ ХАДГАЛАГДСАН ТООГ ШАЛГАНА. Сервер тал мөр бүрийг тусад нь
      //   бичдэг бөгөөд аль нэг нь унавал ҮЛДСЭН нь ч унадаг байв —
      //   гэтэл endpoint үргэлж 201 буцаадаг тул хэрэглэгч
      //   «илгээгдлээ» гэж хараад хариулт нь алга болдог байлаа.
      //   Одоо дутууг ШУУД хэлнэ — дахин илгээх боломж үлдэнэ.
      const sent = res?.answers_sent;
      const ok = res?.answers_saved;
      if (typeof ok === "number" && typeof sent === "number" && ok < sent) {
        await Swal.fire({
          icon: "error",
          title: "Хариулт дутуу хадгалагдлаа",
          html:
            `<div style="text-align:left">${ok}/${sent} хариулт хадгалагдсан.`
            + (res?.answers_failed?.length
              ? `<br/>Асуумж <b>${res.answers_failed.join(", ")}</b> дээр алдаа гарлаа.`
              : "")
            + "<br/><br/>Дахин илгээнэ үү. Давтагдвал ХАБЭА хэлтэст мэдэгдээрэй.</div>",
        });
        return;
      }

      // АШУА-гийн давхар хяналт — офлайн кэшийг хэвээр шинэчилнэ
      // (banner нь localStorage-аас уншсаар байгаа).
      if (isAshua) {
        // ⚠ localStorage-Д БИЧИХГҮЙ. Бөглөлт нь дээрх
        //   `createPreworkSubmission`-аар БААЗАД аль хэдийн орсон
        //   (`checker_role="ASHUA"`). Банер одоо баазаас уншдаг тул
        //   локал кэш нь илүүц бөгөөс өөр төхөөрөмжөөс харагдахгүй
        //   тул төөрөгдүүлнэ.
        await Swal.fire({
          icon: yesCount > 0 ? "warning" : "success",
          title: "АШУА-гийн давхар хяналт бүртгэгдлээ",
          text:
            `${total}/${total} асуумжид хариулсан.`
            + (yesCount > 0
              ? ` ${yesCount} асуумж дээр аюул илрүүлж бүртгэсэн.`
              : " Аюул илрээгүй.")
            + " Энэ нь таны өөрийн хяналт — ажилтан бүр өөрийн 15А-гаа"
            + " тусад нь бөглөнө.",
        });
        navigate("/highrisk/registerTn");
        return;
      }

      await Swal.fire({
        icon: yesCount > 0 ? "warning" : "success",
        title:
          yesCount > 0 ? "Шалгалт илгээгдлээ" : "Pre-work зөвшөөрөл олгогдлоо",
        html:
          `<div style="text-align:left">${total}/${total} асуумжид хариулсан.`
          + (yesCount > 0
            ? ` <b>${yesCount}</b> асуумж дээр аюул илрүүлж бүртгэсэн.`
              + "<br/><br/>Аюул арилтал ажлыг эхлүүлэхгүй. АШУА болон анхан "
              + "шатны удирдах ажилтан хариултыг хараад <b>ажилд гаргах "
              + "эсэхийг</b> шийднэ.</div>"
            : " Аюул илрээгүй. Та ажилдаа орох боломжтой.</div>"),
      });
      navigate("/highrisk/list");
    } catch (e) {
      antMessage.error(
        e?.response?.data?.message || "Илгээхэд алдаа гарлаа",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ӨЭА бүртгэлээ дахин шалгах (АШУА бүртгэсний дараа "Дахин ачаалах")
  const reloadOea = async () => {
    setLoadingOea(true);
    try {
      const r = await getMyOeaRegistration({ tn: myTn });
      setOeaToday(r && r.work_type !== "NARYAD" ? r : null);
    } catch {
      setOeaToday(null);
    } finally {
      setLoadingOea(false);
    }
  };

  if (loadingOea) {
    return (
      <div style={{ background: PAGE_BG, padding: 40 }}>
        Ачааллаж байна...
      </div>
    );
  }

  // АШУА биш + ӨЭА бүртгэлгүй хэрэглэгчид: warning + back-link
  if (!canBeAshua && !oeaToday) {
    return (
      <div style={{ background: PAGE_BG, minHeight: "100%", padding: 16 }}>
        <Result
          status="warning"
          title="Танд өнөөдөр өндөр эрсдэлт ажлын даалгавар алга"
          subTitle="Өндөр Эрсдэлт Ажилд (наряд зөвшөөрөл) гарах 15 асуумжийн урьдчилсан зөвшөөрөл авахын тулд АШУА таныг ӨЭА-гаар бүртгэсэн байх ёстой. Бүртгүүлсний дараа 'Дахин ачаалах' дарна уу."
          extra={
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={reloadOea}
                style={{
                  padding: "10px 22px",
                  background: "#fff",
                  color: RED,
                  border: `2px solid ${RED}`,
                  borderRadius: 8,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <i className="fas fa-redo" style={{ marginRight: 6 }} />
                Дахин ачаалах
              </button>
              <button
                type="button"
                onClick={() => navigate("/highrisk/registerTn")}
                style={{
                  padding: "10px 22px",
                  background: RED,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                АШУА бүртгэлд очих
              </button>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ background: PAGE_BG, minHeight: "100%" }}>
      <style>{`
        /* Үндсэн зохион байгуулалт */
        .pw-wrap { max-width: 720px; margin: 0 auto; padding: 14px; }
        .pw-grid { display: grid; gap: 14px; grid-template-columns: 1fr; }
        .pw-questions { display: grid; gap: 10px; grid-template-columns: 1fr; }
        .pw-sidebar { display: contents; }
        .pw-submit-wrap { width: 100%; }

        /* Tablet — 1.5 баган: асуумж 2 баганатай, sidebar дээр үлдэнэ */
        @media (min-width: 720px) {
          .pw-wrap { max-width: 100%; padding: 18px 24px; }
          .pw-questions { grid-template-columns: repeat(2, 1fr); }
        }

        /* Desktop — Sidebar (зүүн) + асуумжууд (баруун) 2 баганатай */
        @media (min-width: 1100px) {
          .pw-wrap { max-width: 1280px; }
          .pw-grid {
            grid-template-columns: 340px minmax(0, 1fr);
            align-items: start;
          }
          .pw-sidebar {
            display: flex;
            flex-direction: column;
            gap: 14px;
            position: sticky;
            top: 70px;
          }
          .pw-submit-wrap { max-width: 480px; margin-left: auto; }
        }

        @media (min-width: 1500px) {
          .pw-questions { grid-template-columns: repeat(3, 1fr); }
        }

        /* Header — desktop дээр илүү өргөн */
        .pw-header-inner { max-width: 1280px; margin: 0 auto; }
      `}</style>

      {/* Header */}
      <div
        style={{
          background: RED,
          color: "#fff",
          padding: "14px 16px",
          position: "sticky",
          top: 0,
          zIndex: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <div
          className="pw-header-inner"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              padding: 4,
            }}
          >
            <i className="fas fa-arrow-left" />
          </button>
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {isAshua
                ? "АШУА — 15 асуумжийн хяналт"
                : "Pre-work 15 асуумж"}
            </div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>
              {isAshua
                ? `Өнөөдөр бүртгэсэн ${ashuaWorkers.length} ажилтны хариуцлагатай хяналт`
                : "ӨЭА — урьдчилсан шалгалт"}
            </div>
          </div>
          {/* Баруун талын тэнцвэрийн зайг тусламжийн товчоор
              орлуулав — өргөн нь ижил тул гарчиг голдоо хэвээр. */}
          <PageHelp light size={16} />
        </div>

        {/* ── ҮҮРГИЙН СЭЛГЭЛТ ──────────────────────────────────────────
            Нэг хүн өнөөдөр өөрөө ӨЭА-д бүртгэгдсэн БАЙЖ, зэрэг бусдыг ч
            бүртгэсэн (АШУА) байж болно. Тэр хоёр нь ӨӨР ӨӨР бөглөлт:
              • Өөрийн 15А          → ажилд орох нөхцөл (checker_role=WORKER)
              • АШУА давхар хяналт  → журмын шаардлага (checker_role=ASHUA)
            Хоёулаа хамаарах үед л энэ сэлгэлт харагдана. */}
        {canBeAshua && oeaToday ? (
          <div
            className="pw-header-inner"
            style={{ display: "flex", gap: 6, marginTop: 10 }}
          >
            {[
              { key: "WORKER", label: "Өөрийн 15А", icon: "fa-user" },
              {
                key: "ASHUA",
                label: "АШУА давхар хяналт",
                icon: "fa-user-shield",
              },
            ].map((r) => {
              const active = checkerRole === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => {
                    setCheckerRole(r.key);
                    setAnswers({}); // үүрэг солиход хариулт нь тусдаа
                    setReported({});
                  }}
                  style={{
                    flex: 1,
                    padding: "7px 10px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.55)",
                    background: active ? "#fff" : "rgba(255,255,255,0.12)",
                    color: active ? RED : "#fff",
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  <i className={"fas " + r.icon} style={{ marginRight: 6 }} />
                  {r.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Энэ ажил дээр аль хэдийн бүртгэгдсэн, ШИЙДВЭР ХҮЛЭЭГДЭЖ БУЙ аюулууд.
          Хуудсыг дахин нээх бүрд дээд талд нэгдсэн байдлаар харагдана. */}
      {openPriorHazards.length > 0 && (
        <div
          style={{
            margin: "12px auto 0",
            maxWidth: 1200,
            padding: "10px 14px",
            borderRadius: 10,
            background: "#FEEFED",
            border: "1px solid #F2C8C5",
            borderLeft: `4px solid ${RED}`,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: "#8C2A22",
              marginBottom: 4,
            }}
          >
            <i
              className="fas fa-exclamation-triangle fa-exclamation-triangle"
              style={{ marginRight: 7 }}
            />
            Энэ ажил дээр {openPriorHazards.length} аюул бүртгэгдсэн —
            шийдвэр хүлээгдэж байна
          </div>
          <div
            style={{ fontSize: 12, color: "#7A2F2A", lineHeight: 1.5 }}
          >
            Асуумж{" "}
            <b>
              #{openPriorHazards.map((h) => h.prework_item_no).join(", #")}
            </b>{" "}
            дээр бүртгэгдсэн. Эдгээрийг дахин бүртгэх шаардлагагүй — доор
            тухайн асуумж бүрийн дэлгэрэнгүйг харна уу.
          </div>
        </div>
      )}

      {/* Гүйцэтгэх ажлын мэдээллийн banner — ямар ажил, хаана, хэзээ.
          isAshua горимд бус, бүртгэгдсэн ажилтанд харагдана. */}
      {!isAshua && oeaToday && (
        <div
          style={{
            background: "linear-gradient(135deg, #FFF7E6 0%, #FFEFCB 100%)",
            borderBottom: "1.5px solid #FFD591",
            padding: "10px 16px",
          }}
        >
          <div
            style={{
              maxWidth: 1200,
              margin: "0 auto",
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: "#7A4E00",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                background: "#FFD591",
                borderRadius: 6,
                padding: "3px 10px",
              }}
            >
              <i
                className="fas fa-info-circle"
                style={{ marginRight: 5 }}
              />
              Өнөөдрийн ажил
            </span>
            <InfoChip
              icon="fas fa-calendar-alt"
              label="Огноо"
              value={oeaToday.work_date || "—"}
            />
            <InfoChip
              icon="fas fa-clipboard-list"
              label="Ажил"
              value={oeaToday.work_description || "—"}
            />
            <InfoChip
              icon="fas fa-map-marker-alt"
              label="Хаана"
              value={
                oeaToday.destination ||
                oeaToday.section_name ||
                oeaToday.department_name ||
                "—"
              }
            />
            <InfoChip
              icon="fas fa-building"
              label="Хэсэг"
              value={oeaToday.department_name || "—"}
            />
          </div>
        </div>
      )}

      <div className="pw-wrap">
        <div className="pw-grid">
          {/* ── Sidebar ── */}
          <div className="pw-sidebar">
            {isAshua ? (
              /* АШУА горим — давхар хяналтын ажил + бүртгэсэн ажилтнууд */
              <Card>
                {/* ── Аль ӨЭА ажил дээр давхар хяналт хийж байна вэ ── */}
                <div
                  style={{
                    fontSize: 11,
                    color: "#7A8496",
                    fontWeight: 700,
                    letterSpacing: 0.4,
                  }}
                >
                  ДАВХАР ХЯНАЛТ ХИЙЖ БУЙ ӨЭА АЖИЛ
                </div>
                {ashuaJobs.length === 0 ? (
                  <div
                    style={{
                      marginTop: 6,
                      padding: "8px 10px",
                      background: "#FFF7E6",
                      border: "1px solid #FFE0A3",
                      borderRadius: 6,
                      fontSize: 11.5,
                      color: "#7A5B12",
                      lineHeight: 1.45,
                    }}
                  >
                    Өнөөдөр таны бүртгэсэн ӨЭА ажил олдсонгүй. Хяналт нь
                    ажлын дугааргүй хадгалагдана.
                  </div>
                ) : (
                  <select
                    value={ashuaJob?.key ?? ""}
                    onChange={(e) => setAshuaJobId(e.target.value)}
                    disabled={ashuaJobs.length === 1}
                    style={{
                      width: "100%",
                      marginTop: 6,
                      padding: "7px 9px",
                      borderRadius: 6,
                      border: "1px solid #D9DEE7",
                      fontSize: 12,
                      background: "#fff",
                    }}
                  >
                    <option value="">— Ажлаа сонгоно уу —</option>
                    {ashuaJobs.map((j) => (
                      <option key={j.key} value={j.key}>
                        {j.oea_permit_id ? `№${j.oea_permit_id} · ` : ""}
                        {j.destination || j.section_name || j.department_name || "—"}
                        {` (${j.workers.length} хүн)`}
                      </option>
                    ))}
                  </select>
                )}

                <div
                  style={{
                    fontSize: 11,
                    color: "#7A8496",
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    marginTop: 14,
                  }}
                >
                  ӨНӨӨДӨР ТАНЫ БҮРТГЭСЭН АЖИЛТАН
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 900,
                    color: RED,
                    marginTop: 4,
                    lineHeight: 1,
                  }}
                >
                  {ashuaWorkers.length}{" "}
                  <span style={{ fontSize: 12, color: "#9A988F" }}>
                    хүн
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 10,
                    maxHeight: 220,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {ashuaWorkers.map((w, i) => (
                    <div
                      key={w.id || i}
                      style={{
                        padding: "6px 0",
                        borderBottom:
                          i < ashuaWorkers.length - 1
                            ? "1px dashed #EAE9E3"
                            : "none",
                        fontSize: 12,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#2A2F3A",
                        }}
                      >
                        {w.worker_name || w.tn}
                      </div>
                      <div
                        style={{
                          color: "#7A8496",
                          fontSize: 10.5,
                          marginTop: 2,
                        }}
                      >
                        {w.position || "—"}
                        {w.destination && ` · ${w.destination}`}
                      </div>
                    </div>
                  ))}
                </div>
                {ashuaPrev && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "6px 10px",
                      background: "#EAF6EE",
                      border: "1px solid #BEE3C8",
                      borderRadius: 6,
                      fontSize: 11,
                      color: "#1B7F3B",
                      fontWeight: 600,
                    }}
                  >
                    ✓ Өмнө бөглөсөн —{" "}
                    {new Date(ashuaPrev.completed_at).toLocaleTimeString(
                      "mn-MN",
                      { hour: "2-digit", minute: "2-digit" },
                    )}{" "}
                    (Засаж дахин хадгалах боломжтой)
                  </div>
                )}
              </Card>
            ) : (
              /* Ажилтан горим — өөрийн ӨЭА бүртгэл */
              <Card>
                <div
                  style={{
                    fontSize: 11,
                    color: "#7A8496",
                    fontWeight: 700,
                    letterSpacing: 0.4,
                  }}
                >
                  ӨНӨӨДӨР ТАНЫ БҮРТГЭЛ
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    marginTop: 6,
                    color: "#2A2F3A",
                  }}
                >
                  🏢 {oeaToday?.department_name || "—"}
                </div>
                {oeaToday?.section_name &&
                  oeaToday.section_name !== oeaToday.department_name && (
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "#7A8496",
                        marginTop: 2,
                      }}
                    >
                      📍 {oeaToday.section_name}
                    </div>
                  )}
                {oeaToday?.destination && (
                  <div style={{ marginTop: 10 }}>
                    <div
                      style={{
                        fontSize: 10.5,
                        color: "#9A988F",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      Хаашаа
                    </div>
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "#3A4153",
                        marginTop: 2,
                      }}
                    >
                      {oeaToday.destination}
                    </div>
                  </div>
                )}
                {oeaToday?.work_description && (
                  <div style={{ marginTop: 10 }}>
                    <div
                      style={{
                        fontSize: 10.5,
                        color: "#9A988F",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      Ямар ажил
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#3A4153",
                        marginTop: 2,
                        lineHeight: 1.4,
                      }}
                    >
                      {oeaToday.work_description}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Явц */}
            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: "#2A2F3A",
                  }}
                >
                  Явц: {answered}/{total}
                </span>
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 900,
                    color: yesCount > 0 ? "#DC2626" : "#16A34A",
                  }}
                >
                  {pct}%
                </span>
              </div>
              <Progress
                percent={pct}
                strokeColor={yesCount > 0 ? "#DC2626" : "#16A34A"}
                showInfo={false}
              />
              {/* Stat 3 баганат — Тийм / Үгүй / N/A */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <MiniStat
                  label="Аюул"
                  value={yesCount}
                  color={ANSWERS.YES.color}
                  bg={ANSWERS.YES.bg}
                />
                <MiniStat
                  label="ОК"
                  value={items.filter((it) => answers[it.id] === "NO").length}
                  color={ANSWERS.NO.color}
                  bg={ANSWERS.NO.bg}
                />
                <MiniStat
                  label="Алгасах"
                  value={items.filter((it) => answers[it.id] === "NA").length}
                  color={ANSWERS.NA.color}
                  bg={ANSWERS.NA.bg}
                />
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                {yesCount > 0 && (
                  <Tag color="red">⚠ {yesCount} аюул илрүүлсэн</Tag>
                )}
                {unreportedYes.length > 0 && (
                  <Tag color="orange">
                    {unreportedYes.length} бүртгээгүй
                  </Tag>
                )}
                {isComplete && unreportedYes.length === 0 && (
                  <Tag color={yesCount > 0 ? "gold" : "green"}>
                    {yesCount > 0 ? "✓ Илгээхэд бэлэн" : "✓ Бэлэн"}
                  </Tag>
                )}
              </div>
            </Card>
          </div>

          {/* ── Гол агуулга — асуумжуудын жагсаалт ── */}
          <div>
            <div className="pw-questions" style={{ marginBottom: 12 }}>
              {items.map((it) => {
            const ans = answers[it.id];
            const ansCfg = ans ? ANSWERS[ans] : null;
            return (
              <Card
                key={it.id}
                style={{
                  borderLeft: ansCfg
                    ? `4px solid ${ansCfg.color}`
                    : "4px solid transparent",
                  background: ansCfg ? ansCfg.bg + "20" : "#fff",
                  transition: "all 0.18s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: ansCfg ? ansCfg.bg : "#F1F3F6",
                      color: ansCfg ? ansCfg.color : "#7A8496",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: 14,
                      border:
                        "2px solid " +
                        (ansCfg ? ansCfg.color : "#E6E9EF"),
                    }}
                  >
                    {String(it.no).padStart(2, "0")}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "#2A2F3A",
                        lineHeight: 1.4,
                      }}
                    >
                      {it.title}
                    </div>
                    {it.description && (
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "#7A8496",
                          marginTop: 4,
                          lineHeight: 1.4,
                        }}
                      >
                        {it.description}
                      </div>
                    )}

                    {/* ЭНЭ ҮЗҮҮЛЭЛТ дээр урьд бүртгэсэн аюул —
                        дугаар, төлөв, тайлбар. Шийдвэр гараагүй бол
                        «хүлээгдэж байна» гэж онцолно. */}
                    {priorHazards[Number(it.no)] ? (
                      <PriorHazardNote h={priorHazards[Number(it.no)]} />
                    ) : null}

                    {/* Хариултын товчнууд */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 6,
                        marginTop: 10,
                      }}
                    >
                      {Object.values(ANSWERS).map((a) => {
                        const active = ans === a.key;
                        return (
                          <button
                            key={a.key}
                            type="button"
                            onClick={() => setAnswer(it.id, a.key)}
                            style={{
                              padding: "8px 4px",
                              borderRadius: 8,
                              border:
                                "1.5px solid " +
                                (active ? a.color : "#E6E9EF"),
                              background: active ? a.color : "#fff",
                              color: active ? "#fff" : a.color,
                              fontSize: 12.5,
                              fontWeight: 700,
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            {a.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {ans === "YES" && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "8px 10px",
                      background: "#FEEFED",
                      border: "1px solid #F2C8C5",
                      borderRadius: 8,
                      fontSize: 11.5,
                      color: "#B5251D",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <i className="fas fa-exclamation-triangle" />
                    Аюул илрэв — "Ажил зогсоох" хэлбэрээр мэдээлэх шаардлагатай.
                    <button
                      type="button"
                      onClick={() => openHazardReport(it)}
                      style={{
                        marginLeft: "auto",
                        background: RED,
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "4px 10px",
                        fontSize: 11.5,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Мэдээлэх →
                    </button>
                  </div>
                )}
              </Card>
              );
            })}
            </div>

            {/* Эцсийн товч — баруун дээд буланд багтсан */}
            <div className="pw-submit-wrap">
              <button
                type="button"
                onClick={completePreWork}
                // Товчийг ТҮГЖИХГҮЙ — дутуу үед дарахад юу дутууг хэлнэ.
                // (Саарал, дардаггүй товч нь "систем эвдэрсэн" мэт
                //  ойлгогдож, ажилтан аюулаа бүртгээд орхидог байв.)
                disabled={submitting}
                style={{
                  width: "100%",
                  padding: "14px 0",
                  borderRadius: 10,
                  border: "none",
                  background: !isComplete
                    ? "#CBD5E1"
                    : yesCount > 0
                      ? RED
                      : "#16A34A",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 800,
                  cursor: submitting ? "not-allowed" : "pointer",
                  boxShadow: isComplete
                    ? yesCount > 0
                      ? "0 4px 12px rgba(226, 56, 46, 0.3)"
                      : "0 4px 12px rgba(22, 163, 74, 0.3)"
                    : "none",
                }}
              >
                <i
                  className={
                    "fas "
                    + (submitting
                      ? "fa-spinner fa-spin"
                      : yesCount > 0
                        ? "fa-paper-plane"
                        : "fa-check-circle")
                  }
                  style={{ marginRight: 6 }}
                />
                {submitting
                  ? "Илгээж байна..."
                  : !isComplete
                    ? `Дутуу ${total - answered} асуумж — үргэлжлүүлнэ үү`
                    : isAshua
                      ? "АШУА хяналт хадгалах"
                      : yesCount > 0
                        ? "Шалгалтыг илгээх"
                        : "Pre-work зөвшөөрөл авах"}
              </button>
              {/* Аюул бүртгэсэн ч шалгалт ИЛГЭЭГЭЭГҮЙ бол бүртгэл дуусаагүй.
                  Энэ алхмыг алгасвал /me дээр "бөглөх" хэвээр харагдана. */}
              {!isComplete && yesCount > 0 ? (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 12px",
                    borderRadius: 8,
                    background: "#FFF7E6",
                    border: "1px solid #FFE0A3",
                    color: "#7A5B12",
                    fontSize: 12,
                    lineHeight: 1.45,
                  }}
                >
                  <i
                    className="fas fa-info-circle"
                    style={{ marginRight: 6 }}
                  />
                  Аюулаа бүртгэсэн нь <b>шалгалтыг дуусгахгүй</b>. Үлдсэн{" "}
                  <b>{total - answered}</b> асуумжид хариулж, доорх товчоор
                  илгээж дуусгана уу.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* "Ажил зогсоох" модал — HazardFormComposer mode="stop_work" */}
      <Modal
        open={hazardOpen}
        onCancel={closeHazard}
        footer={null}
        width={720}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              color: RED,
              paddingRight: 28,
            }}
          >
            <i
              className="fas fa-hand-paper"
              style={{ fontSize: 18, flexShrink: 0 }}
            />
            <span
              style={{
                fontWeight: 800,
                flex: "1 1 auto",
                minWidth: 0,
                fontSize: 14,
                lineHeight: 1.3,
              }}
            >
              Ажил зогсоох
            </span>
            {hazardFlagged && (
              <Tag
                color="red"
                style={{
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  fontSize: 11,
                  lineHeight: 1.3,
                  padding: "2px 8px",
                  margin: 0,
                }}
              >
                Чухал хяналтын асуумж #{hazardFlagged.no} — «
                {hazardFlagged.title}» → ТИЙМ (аюул байна)
              </Tag>
            )}
          </div>
        }
      >
        <HazardFormComposer
          mode="stop_work"
          value={hazardForm}
          onChange={setHazardForm}
          categories={categories}
          hazards={hazardList}
          // «Аюулын нэр / нэрс» сонгогч — бүртгэлд илүүдэл алхам тул нуув
          showHazardPicker={false}
          severities={severities}
          onPickFile={uploadHazardPhoto}
          onSave={submitHazardStop}
          onCancel={closeHazard}
          saving={saving}
        />
      </Modal>
    </div>
  );
}

// ── Info chip — banner-д ашиглах label/value хослол ──────────────────────
const InfoChip = ({ icon, label, value }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: "#fff",
      border: "1px solid #FFD591",
      borderRadius: 6,
      padding: "5px 10px",
      minWidth: 0,
    }}
  >
    <span
      style={{
        fontSize: 14,
        color: "#D46B08",
        flexShrink: 0,
      }}
    >
      <i className={icon} />
    </span>
    <div style={{ minWidth: 0, lineHeight: 1.2 }}>
      <div
        style={{
          fontSize: 9,
          color: "#9A988F",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: "#2C2C2A",
          fontWeight: 700,
          maxWidth: 280,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={value}
      >
        {value}
      </div>
    </div>
  </div>
);

// ── Жижиг тоон карт (Аюул / ОК / Алгасах) ────────────────────────────────
const MiniStat = ({ label, value, color, bg }) => (
  <div
    style={{
      background: bg,
      borderRadius: 8,
      padding: "8px 6px",
      textAlign: "center",
      border: `1px solid ${color}33`,
    }}
  >
    <div style={{ fontSize: 18, fontWeight: 900, color, lineHeight: 1 }}>
      {value}
    </div>
    <div
      style={{
        fontSize: 10,
        color,
        fontWeight: 700,
        marginTop: 4,
        textTransform: "uppercase",
        letterSpacing: 0.3,
      }}
    >
      {label}
    </div>
  </div>
);
